/**
 * Chart helpers for BitBot Dashboard.
 * Pure functions for chart data processing and display components.
 */

/**
 * Format price with appropriate decimal places based on value.
 */
export const formatPrice = (price, pair) => {
  if (!price || price === 0) return '0.00';
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Get RSI status color based on thresholds.
 */
export const getRsiColor = (rsi, oversold, overbought) => {
  if (rsi <= oversold) return '#00FF41';
  if (rsi >= overbought) return '#FF0000';
  return '#F3F9D2';
};

/**
 * Get RSI status text.
 */
export const getRsiStatus = (rsi, oversold, overbought) => {
  if (rsi <= oversold) return 'OVERSOLD';
  if (rsi >= overbought) return 'OVERBOUGHT';
  return 'NEUTRAL';
};

/**
 * Enhance chart data with moving averages, high/low bands, and change calculations.
 */
export const getEnhancedChartData = (history) => {
  if (!history || history.length === 0) return [];

  return history.map((point, index, arr) => {
    const price = point.price || point.close || 0;

    // SMA over last 10 points
    const smaWindow = arr.slice(Math.max(0, index - 9), index + 1);
    const sma = smaWindow.reduce((sum, p) => sum + (p.price || p.close || 0), 0) / smaWindow.length;

    // Weighted EMA over last 6 points
    const emaWindow = arr.slice(Math.max(0, index - 5), index + 1);
    const ema = emaWindow.length > 0
      ? emaWindow.reduce((sum, p, i) => sum + (p.price || p.close || 0) * (i + 1), 0) /
        (emaWindow.length * (emaWindow.length + 1) / 2)
      : price;

    const volatility = price * 0.002;
    const high = point.high || (price + volatility * (0.5 + Math.random() * 0.5));
    const low = point.low || (price - volatility * (0.5 + Math.random() * 0.5));

    const prevPrice = index > 0 ? (arr[index - 1].price || arr[index - 1].close || price) : price;
    const change = price - prevPrice;
    const changePercent = prevPrice > 0 ? (change / prevPrice) * 100 : 0;

    return {
      ...point,
      price,
      high,
      low,
      sma: Math.round(sma * 100) / 100,
      ema: Math.round(ema * 100) / 100,
      change,
      changePercent,
      rsi: point.rsi || null,
    };
  });
};

/**
 * Get chart data for a selected pair from the price history maps.
 */
export const getSelectedChartData = (selectedChart, priceHistories) => {
  const rawData = priceHistories[selectedChart] || priceHistories['BTC-USD'] || [];
  return getEnhancedChartData(rawData);
};

/**
 * Custom tooltip component for the price chart.
 */
export const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    const price = payload[0]?.value;
    const priceDisplay = formatPrice(price);
    const change = data?.change || 0;
    const changePercent = data?.changePercent || 0;
    const high = data?.high;
    const low = data?.low;

    return (
      <div className="bg-black/95 border border-[#00FF41]/50 p-3 text-sm rounded shadow-lg shadow-[#00FF41]/10">
        <div className="text-gray-400 text-xs mb-2 font-mono">{label || data?.time}</div>
        <div className="space-y-1">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Price</span>
            <span className="text-[#00FF41] font-bold font-mono">{priceDisplay}</span>
          </div>
          {high && low && (
            <>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">High</span>
                <span className="text-green-400 font-mono text-xs">{formatPrice(high)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Low</span>
                <span className="text-red-400 font-mono text-xs">{formatPrice(low)}</span>
              </div>
            </>
          )}
          {change !== 0 && (
            <div className="flex justify-between gap-4 pt-1 border-t border-gray-800">
              <span className="text-gray-500">Change</span>
              <span className={`font-mono ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? '+' : ''}{formatPrice(change)} ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%)
              </span>
            </div>
          )}
          {payload[1] && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">RSI</span>
              <span className={`font-mono ${payload[1]?.value <= 30 ? 'text-green-400' : payload[1]?.value >= 70 ? 'text-red-400' : 'text-yellow-400'}`}>
                {payload[1]?.value?.toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};
